import { getDirectionalLight, getSpotLight, getAmbientLight } from '@/commons/lights/LightProvider';
import { Wedding3DWorld } from '@/littleWedding/Wedding3DWorld';
import { LightProbe, SpotLight, type CubeTexture, Object3D, AmbientLight, DirectionalLight, RectAreaLight } from 'three';
// @ts-expect-error: Importing from 'three/examples/jsm/lights/LightProbeGenerator' might not have type definitions
import { LightProbeGenerator } from 'three/examples/jsm/lights/LightProbeGenerator';
import type { ILightThreeParams } from '@/commons/lights/typescript/interface/ILightThreeParams';
import type { IThreeLights } from '@/commons/lights/typescript/type/TThreeLights';

// Function to import config file and load it to the scene
// Can take a light creation file and default light parameter
// include config file in the scene
// can create and load light based on json file

// Physical correct light conversion :
// 1.000 W/m2=683.0 lumen/m2 (Lux) at the wavelength of 555.0 nm

const LightHelper = {
    lightNames: {
        sun: 'sunSpot',
        shadow: 'shadowSpot',
        ambient: 'ambientLight',
    },
    // Allow to just replace World here so scene is working
    init() {
        const ambientLight = new AmbientLight('rgb(255, 255, 255)', 0.2);
        ambientLight.name = 'ambientLight';
        Wedding3DWorld.scene?.add(ambientLight);
    },

    areaLights() {
        const { shadow } = LightHelper.lightNames;

        const shadowSpotLight = new SpotLight('rgb(10, 10, 10)', 1, 10, Math.PI * 0.2);
        shadowSpotLight.name = shadow;
        shadowSpotLight.angle = 0.2;
        shadowSpotLight.penumbra = 0.3;
        shadowSpotLight.position.x = 0;
        shadowSpotLight.position.y = 15;
        shadowSpotLight.position.z = 0;
        shadowSpotLight.distance = 20;
        shadowSpotLight.castShadow = true;
        shadowSpotLight.power = 100;
        shadowSpotLight.shadow.bias = 0.2;
        shadowSpotLight.shadow.normalBias = 2;
        shadowSpotLight.shadow.radius = 100;
        shadowSpotLight.shadow.blurSamples = 64;
        shadowSpotLight.shadow.camera.near = 1;
        shadowSpotLight.shadow.camera.far = 6;
        shadowSpotLight.shadow.mapSize.set(4096, 4096);
        shadowSpotLight.target = new Object3D();
        Wedding3DWorld.scene?.add(shadowSpotLight);
        // FIXME: Make it parametric
        const distance = 1.5;
        const height = 0.75;
        const lightPosition = [1, 2, 3].map((x) => {
            return {
                x: Math.cos(((Math.PI * 2) / 3) * x) * distance,
                y: height,
                z: Math.sin(((Math.PI * 2) / 3) * x) * distance,
            };
        });
        const panelWidth = 2 * Math.sqrt(3) * distance;
        const panelHeight = 4;
        const intensity = 100;
        const [first, second, third] = lightPosition;
        const firstAreaLight = new RectAreaLight(0xffffff, 0, panelWidth, panelHeight);
        firstAreaLight.power = intensity;
        firstAreaLight.name = 'firstAreaLight';
        firstAreaLight.position.set(first.x, first.y, first.z);
        firstAreaLight.lookAt(0, height, 0);
        Wedding3DWorld.instance?.camera.add(firstAreaLight);
        Wedding3DWorld.scene?.add(firstAreaLight);

        const secondAreaLight = new RectAreaLight(0xffffff, 0, panelWidth, panelHeight);
        secondAreaLight.power = intensity;
        secondAreaLight.name = 'secondAreaLight';
        secondAreaLight.position.set(second.x, second.y, second.z);
        secondAreaLight.lookAt(0, height, 0);
        Wedding3DWorld.instance?.camera.add(secondAreaLight);
        Wedding3DWorld.scene?.add(secondAreaLight);

        const thirdAreaLight = new RectAreaLight(0xffffff, 0, panelWidth, panelHeight);
        thirdAreaLight.power = intensity;
        thirdAreaLight.name = 'thirdAreaLight';
        thirdAreaLight.position.set(third.x, third.y, third.z);
        thirdAreaLight.lookAt(0, height, 0);
        Wedding3DWorld.scene?.add(thirdAreaLight);
    },

    defaultLight() {
        const { ambient, shadow, sun } = LightHelper.lightNames;
        const spotLight = new SpotLight('rgb(173, 93, 15)', 1, 10, Math.PI * 0.2);
        spotLight.position.y = 0.6;
        spotLight.position.x = 1;
        spotLight.position.z = 0;
        spotLight.penumbra = 0.3;
        spotLight.distance = 10;
        spotLight.castShadow = false;
        spotLight.name = sun;
        spotLight.power = 8000;
        spotLight.shadow.bias = -0.002;
        spotLight.shadow.normalBias = 0.2;
        spotLight.shadow.radius = 35;
        spotLight.shadow.blurSamples = 32;
        spotLight.shadow.camera.near = 0.63;
        spotLight.shadow.camera.far = 1.4;
        spotLight.shadow.mapSize.set(4096, 4096);
        Wedding3DWorld.scene?.add(spotLight);

        const spotLight2 = new SpotLight('rgb(173, 93, 15)', 1, 10, Math.PI * 0.2);
        spotLight2.position.y = 0.6;
        spotLight2.position.x = -1;
        spotLight2.position.z = 0;
        spotLight2.penumbra = 0.3;
        spotLight2.distance = 10;
        spotLight2.castShadow = false;
        spotLight2.name = 'seconSun';
        spotLight2.power = 7;
        spotLight2.shadow.bias = -0.002;
        spotLight2.shadow.normalBias = 0.2;
        spotLight2.shadow.radius = 35;
        spotLight2.shadow.blurSamples = 32;
        spotLight2.shadow.camera.near = 0.63;
        spotLight2.shadow.camera.far = 1.4;
        spotLight2.shadow.mapSize.set(4096, 4096);
        Wedding3DWorld.scene?.add(spotLight2);

        const shadowSpotLight = new SpotLight('rgb(10,10,10)', 1, 10, Math.PI * 0.2);
        shadowSpotLight.position.x = 0;
        shadowSpotLight.position.y = 1.5;
        shadowSpotLight.position.z = 0;
        shadowSpotLight.penumbra = 0.3;
        shadowSpotLight.distance = 3;
        shadowSpotLight.castShadow = false;
        shadowSpotLight.name = shadow;
        shadowSpotLight.power = 100;
        shadowSpotLight.shadow.bias = -0.002;
        shadowSpotLight.shadow.normalBias = 0.2;
        shadowSpotLight.shadow.radius = 35;
        shadowSpotLight.shadow.blurSamples = 32;
        shadowSpotLight.shadow.camera.near = 1.1;
        shadowSpotLight.shadow.camera.far = 4.2;
        shadowSpotLight.shadow.mapSize.set(4096, 4096);
        Wedding3DWorld.scene?.add(shadowSpotLight);

        const ambientLight = new AmbientLight('rgb(255, 255, 255)', 0.2);
        ambientLight.name = ambient;
        Wedding3DWorld.scene?.add(ambientLight);
    },

    initLightProbeCallBack: (cubeMap: CubeTexture) => {
        const lightProb = new LightProbe();
        lightProb.name = 'lightProbe';
        Wedding3DWorld.scene?.add(lightProb);
        lightProb.copy(LightProbeGenerator.fromCubeTexture(cubeMap));
    },

    addLight(lightsParams: ILightThreeParams): IThreeLights | null {
        let target: Object3D | undefined;
        let light: IThreeLights | null = null;
        switch (lightsParams.type) {
            case 'AmbientLight':
                light = getAmbientLight(lightsParams);
                break;
            case 'DirectionalLight':
                light = getDirectionalLight(lightsParams);
                break;
            case 'SpotLight':
                light = getSpotLight(lightsParams);
                break;
            default:
                break;
        }
        if (!light) {
            return null;
        }
        if (lightsParams.name) {
            light.name = lightsParams.name;
        }
        // If target is undefined, the default target is [0, 0, 0]
        Wedding3DWorld.scene?.add(light);
        if (target) {
            const { x, y, z } = target.position;
            light.position.set(0, y + 5, 0);
            if (light instanceof SpotLight || light instanceof DirectionalLight) {
                light.target.position.set(x, y, z);
                Wedding3DWorld.scene?.add(light.target);
            }
        }
        return light; // usefull for debug panel
    },

    removeLight(light: IThreeLights) {
        Wedding3DWorld.scene?.remove(light);
    },
};

export { LightHelper };
